// ABOUTME: Feedback modal (SHModal): category + narrative form with an auto-captured screenshot
// ABOUTME: attached by default, removable, and editable with a freehand Gaussian-blur brush before submit.

import { Box, MenuItem, Select, TextField, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import SHModal from "../theme/Modal/SHModal";
import Shbutton from "../theme/buttons/Shbutton";
import ButtonGroup from "../theme/buttons/ButtonGroup";
import Subheading from "../theme/text/Subheading";
import Text from "../theme/text/Text";
import { useSubmitFeedbackMutation } from "../../newapi/feedback/feedbackApiSlice";
import { getFeedbackContext } from "../../utils/feedbackContext";
import { showToastMessage, showToastMessageRequestError } from "../../utils/toasts";

const CATEGORIES = ["Bug", "Suggestion", "Question"];
const BRUSH = 28;
const MAX_W = 900;

const MFeedback = (props) => {
    const { open, onClose, initialScreenshot } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [category, setCategory] = useState("Bug");
    const [narrative, setNarrative] = useState("");
    const [hasScreenshot, setHasScreenshot] = useState(false);
    const [editing, setEditing] = useState(false);
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);

    const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();

    // Reset the form each time the modal opens; the screenshot is supplied by the
    // parent (captured before the modal mounted) and drawn into the canvas below.
    useEffect(() => {
        if (open) {
            setCategory("Bug");
            setNarrative("");
            setEditing(false);
            setHasScreenshot(Boolean(initialScreenshot));
        }
    }, [open, initialScreenshot]);

    // Draw the captured screenshot into the canvas once it (and the canvas) are present.
    useEffect(() => {
        if (!open || !initialScreenshot || !hasScreenshot) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const img = new Image();
        img.onload = () => {
            const target = canvasRef.current;
            if (!target) return;
            const ratio = Math.min(1, MAX_W / img.width);
            target.width = img.width * ratio;
            target.height = img.height * ratio;
            const ctx = target.getContext("2d");
            ctx.drawImage(img, 0, 0, target.width, target.height);
        };
        img.src = initialScreenshot;
    }, [open, initialScreenshot, hasScreenshot]);

    // Freehand Gaussian-blur brush: re-draw a blurred patch under the pointer.
    const blurAt = (x, y) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, BRUSH, 0, Math.PI * 2);
        ctx.clip();
        ctx.filter = "blur(8px)";
        ctx.drawImage(canvas, 0, 0);
        ctx.restore();
    };

    const pointerPos = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const scaleX = canvasRef.current.width / rect.width;
        const scaleY = canvasRef.current.height / rect.height;
        return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    };

    const onPointerDown = (e) => { if (!editing) return; drawingRef.current = true; const p = pointerPos(e); blurAt(p.x, p.y); };
    const onPointerMove = (e) => { if (!editing || !drawingRef.current) return; const p = pointerPos(e); blurAt(p.x, p.y); };
    const onPointerUp = () => { drawingRef.current = false; };

    const removeScreenshot = () => {
        setHasScreenshot(false);
        setEditing(false);
    };

    const canvasToBlob = () =>
        new Promise((resolve) => {
            if (!hasScreenshot || !canvasRef.current) return resolve(null);
            canvasRef.current.toBlob((blob) => resolve(blob), "image/png");
        });

    const handleSend = async () => {
        if (!narrative.trim()) {
            showToastMessage("warning", t("feedback_narrative_required"));
            return;
        }
        try {
            const blob = await canvasToBlob();
            const screenshot = blob ? new File([blob], "screenshot.png", { type: "image/png" }) : null;
            const result = await submitFeedback({
                category,
                narrative,
                contextJson: JSON.stringify(getFeedbackContext()),
                screenshot,
            }).unwrap();
            showToastMessage("success", `${t("feedback_submitted")} ${result.referenceId}`);
            onClose();
        } catch (err) {
            showToastMessageRequestError(t("feedback_submit_failed"), t, err);
        }
    };

    const accent = theme.palette.mode === "dark" ? "blue" : "primary";

    return (
        <SHModal className="mfeedback-modal" open={open} onClose={onClose}>
            <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 2, minWidth: 500 }}>
                <Subheading>{t("feedback_title")}</Subheading>

                <Text light>{t("feedback_category")}</Text>
                <Select value={category} onChange={(e) => setCategory(e.target.value)} size="small">
                    {CATEGORIES.map((c) => (
                        <MenuItem key={c} value={c}>{t(`feedback_category_${c.toLowerCase()}`)}</MenuItem>
                    ))}
                </Select>

                <Text light>{t("feedback_narrative")}</Text>
                <TextField
                    multiline minRows={4} value={narrative}
                    onChange={(e) => setNarrative(e.target.value)}
                    placeholder={t("feedback_narrative_placeholder")}
                />

                {hasScreenshot && (
                    <>
                        <Text light>{editing ? t("feedback_blur_hint") : t("feedback_screenshot")}</Text>
                        <Box sx={{ maxHeight: 360, overflow: "auto", border: `1px solid ${colors.grey[300]}`, borderRadius: 2 }}>
                            <canvas
                                ref={canvasRef}
                                style={{ width: "100%", display: "block", cursor: editing ? "crosshair" : "default", touchAction: "none" }}
                                onPointerDown={onPointerDown}
                                onPointerMove={onPointerMove}
                                onPointerUp={onPointerUp}
                                onPointerLeave={onPointerUp}
                            />
                        </Box>
                        <ButtonGroup version={2}>
                            <Shbutton onClick={removeScreenshot} variant="outlined" color="red">
                                {t("feedback_remove_screenshot")}
                            </Shbutton>
                            <Shbutton onClick={() => setEditing((v) => !v)} variant="outlined" color={accent}>
                                {editing ? t("feedback_edit_done") : t("feedback_edit_blur")}
                            </Shbutton>
                        </ButtonGroup>
                    </>
                )}

                <ButtonGroup version={2}>
                    <Shbutton onClick={handleSend} isLoading={isLoading} variant="contained" color={accent}>
                        {t("feedback_send")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MFeedback;
